import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { BookText, Lock, Search, X, ArrowRight, ChevronDown } from "lucide-react";

function formatVerseRange(v_start, v_end) {
  if (!v_start) return "";
  if (v_end && v_end !== v_start) return `v.${v_start}–${v_end}`;
  return `v.${v_start}`;
}

export default function Commentary() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api
      .get("/members/commentary")
      .then((r) => {
        setData(r.data);
        if (r.data.books && r.data.books.length > 0) setSelectedBook(r.data.books[0].book);
      })
      .catch((e) => setError(e?.response?.status === 403 ? "membership" : "unknown"))
      .finally(() => setLoading(false));
  }, [user]);

  const otBooks = useMemo(() => (data?.books || []).filter((b) => b.testament === "OT"), [data]);
  const ntBooks = useMemo(() => (data?.books || []).filter((b) => b.testament === "NT"), [data]);
  const currentBook = useMemo(
    () => (data?.books || []).find((b) => b.book === selectedBook),
    [data, selectedBook]
  );

  const filteredChapters = useMemo(() => {
    if (!currentBook) return [];
    if (!search.trim()) return currentBook.chapters;
    const q = search.toLowerCase();
    return currentBook.chapters
      .map((c) => ({
        chapter: c.chapter,
        notes: c.notes.filter(
          (n) =>
            (n.ref || "").toLowerCase().includes(q) ||
            (n.note || "").toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.notes.length > 0);
  }, [currentBook, search]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <h1 className="font-serif-display text-3xl mt-6" style={{ color: "var(--ink)" }}>Sign in to continue</h1>
        <Link to="/login?next=/dashboard/commentary" data-testid="commentary-login-redirect" className="btn-primary mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }

  if (error === "membership" || (!loading && !user.membership_active && !user.is_admin)) {
    return (
      <div data-testid="commentary-paywall" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--terracotta)", margin: "0 auto" }} />
        <div className="overline mt-6">Members only</div>
        <h1 className="font-serif-display text-4xl mt-3" style={{ color: "var(--ink)" }}>Commentary is for members.</h1>
        <Link to="/membership" data-testid="commentary-subscribe-link" className="btn-primary mt-8 inline-flex">Become a member</Link>
      </div>
    );
  }

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 py-16"><p style={{ color: "var(--muted)" }}>Loading commentary…</p></div>;
  }

  const totalNotes = data?.total_notes || 0;
  const booksCount = data?.books_covered || 0;

  return (
    <div data-testid="commentary-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
      <header className="mb-10">
        <div className="overline mb-3">Members · commentary</div>
        <h1 className="font-serif-display text-4xl lg:text-6xl tracking-[-0.03em] leading-[1]" style={{ color: "var(--ink)" }}>
          Cody's <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>commentary</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base lg:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Every verse note from Read With Me — organized by book, chapter, and verse.
          A running commentary that grows every day the ministry does.
        </p>
        <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gold)" }}>
          <BookText size={14} strokeWidth={2} />
          <span>
            {totalNotes.toLocaleString()} note{totalNotes === 1 ? "" : "s"} · {booksCount} book{booksCount === 1 ? "" : "s"} covered
          </span>
        </div>
      </header>

      {data?.books?.length === 0 ? (
        <div className="card-surface p-16 text-center" data-testid="commentary-empty">
          <BookText size={32} strokeWidth={1.5} style={{ color: "var(--muted)", margin: "0 auto" }} />
          <h3 className="font-serif-display text-2xl mt-5" style={{ color: "var(--ink)" }}>
            The commentary is empty for now.
          </h3>
          <p className="mt-3 text-sm max-w-md mx-auto" style={{ color: "var(--ink-2)" }}>
            As Cody adds verse notes to daily readings, they'll appear here — grouped by book and chapter.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Book navigation */}
          <aside className="lg:col-span-3">
            {/* Mobile — dropdown */}
            <div className="lg:hidden relative mb-4">
              <select
                value={selectedBook || ""}
                onChange={(e) => setSelectedBook(e.target.value)}
                className="w-full appearance-none py-3 pl-4 pr-10 border text-sm font-serif-display"
                data-testid="commentary-book-select-mobile"
              >
                {otBooks.length > 0 && <optgroup label="Old Testament">
                  {otBooks.map((b) => (
                    <option key={b.book} value={b.book}>{b.book} ({b.note_count})</option>
                  ))}
                </optgroup>}
                {ntBooks.length > 0 && <optgroup label="New Testament">
                  {ntBooks.map((b) => (
                    <option key={b.book} value={b.book}>{b.book} ({b.note_count})</option>
                  ))}
                </optgroup>}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
            </div>

            {/* Desktop — vertical list */}
            <nav className="hidden lg:block sticky top-8" data-testid="commentary-book-nav">
              {otBooks.length > 0 && (
                <>
                  <div className="overline mb-3" style={{ fontSize: "0.6rem" }}>Old Testament</div>
                  <ul className="space-y-1 mb-6">
                    {otBooks.map((b) => (
                      <li key={b.book}>
                        <button
                          onClick={() => setSelectedBook(b.book)}
                          data-testid={`commentary-book-${b.book.replace(/ /g, "_")}`}
                          className="w-full text-left text-sm py-1.5 px-3 transition-colors flex items-center justify-between"
                          style={{
                            color: selectedBook === b.book ? "var(--ember)" : "var(--ink-2)",
                            fontWeight: selectedBook === b.book ? 700 : 500,
                            background: selectedBook === b.book ? "var(--surface-hover)" : "transparent",
                            borderLeft: `2px solid ${selectedBook === b.book ? "var(--ember)" : "transparent"}`,
                          }}
                        >
                          <span>{b.book}</span>
                          <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{b.note_count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {ntBooks.length > 0 && (
                <>
                  <div className="overline mb-3" style={{ fontSize: "0.6rem" }}>New Testament</div>
                  <ul className="space-y-1">
                    {ntBooks.map((b) => (
                      <li key={b.book}>
                        <button
                          onClick={() => setSelectedBook(b.book)}
                          data-testid={`commentary-book-${b.book.replace(/ /g, "_")}`}
                          className="w-full text-left text-sm py-1.5 px-3 transition-colors flex items-center justify-between"
                          style={{
                            color: selectedBook === b.book ? "var(--ember)" : "var(--ink-2)",
                            fontWeight: selectedBook === b.book ? 700 : 500,
                            background: selectedBook === b.book ? "var(--surface-hover)" : "transparent",
                            borderLeft: `2px solid ${selectedBook === b.book ? "var(--ember)" : "transparent"}`,
                          }}
                        >
                          <span>{b.book}</span>
                          <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{b.note_count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </nav>
          </aside>

          {/* Main — chapters & notes */}
          <main className="lg:col-span-9">
            {currentBook && (
              <>
                <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
                  <h2 className="font-serif-display text-3xl lg:text-5xl tracking-[-0.03em] leading-[1]" style={{ color: "var(--ink)" }}>
                    {currentBook.book}
                  </h2>
                  <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gold)" }}>
                    {currentBook.note_count} note{currentBook.note_count === 1 ? "" : "s"} · {currentBook.chapters.length} chapter{currentBook.chapters.length === 1 ? "" : "s"}
                  </div>
                </div>

                {/* Chapter jump-nav — sticky pills */}
                {filteredChapters.length > 1 && (
                  <div
                    className="sticky top-0 z-10 -mx-2 px-2 py-3 mb-6 backdrop-blur-md flex items-center gap-2 flex-wrap"
                    style={{
                      background: "color-mix(in srgb, var(--bg-base) 88%, transparent)",
                      borderBottom: "1px solid var(--border-subtle)",
                    }}
                    data-testid="commentary-chapter-nav"
                  >
                    <span className="text-[0.6rem] uppercase tracking-[0.2em] mr-1" style={{ color: "var(--muted)" }}>
                      Jump to
                    </span>
                    {filteredChapters.map((chap) => (
                      <a
                        key={chap.chapter}
                        href={`#chapter-${chap.chapter}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(`chapter-${chap.chapter}`);
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        data-testid={`commentary-chapter-pill-${chap.chapter}`}
                        className="text-xs px-3 py-1.5 border transition-colors hover:border-[var(--ember)] hover:text-[var(--ember)]"
                        style={{
                          borderColor: "var(--border-strong)",
                          color: "var(--ink-2)",
                          fontFamily: "'Montserrat', sans-serif",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                        }}
                        title={`Chapter ${chap.chapter} — ${chap.notes.length} note${chap.notes.length === 1 ? "" : "s"}`}
                      >
                        {chap.chapter}
                        <span className="ml-1.5 text-[0.6rem]" style={{ color: "var(--muted)" }}>
                          {chap.notes.length}
                        </span>
                      </a>
                    ))}
                  </div>
                )}

                {/* Search within book */}
                <div className="relative mb-8">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--muted)" }} />
                  <input
                    data-testid="commentary-search"
                    type="text"
                    placeholder={`Search notes in ${currentBook.book}…`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-11 pr-10 py-3 text-sm bg-transparent border outline-none"
                    style={{ borderColor: "var(--border-subtle)", color: "var(--ink)" }}
                  />
                  {search && (
                    <button onClick={() => setSearch("")} data-testid="commentary-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 p-1" style={{ color: "var(--muted)" }}>
                      <X size={14} />
                    </button>
                  )}
                </div>

                {filteredChapters.length === 0 ? (
                  <p style={{ color: "var(--muted)" }}>No notes match "{search}" in {currentBook.book}.</p>
                ) : (
                  <div className="space-y-12">
                    {filteredChapters.map((chap) => (
                      <section
                        key={chap.chapter}
                        id={`chapter-${chap.chapter}`}
                        data-testid={`commentary-chapter-${chap.chapter}`}
                        className="scroll-mt-24"
                      >
                        <div className="flex items-baseline gap-4 mb-6">
                          <div className="font-serif-display text-2xl" style={{ color: "var(--ember)" }}>
                            Chapter {chap.chapter}
                          </div>
                          <div className="editorial-rule flex-1" />
                        </div>
                        <div className="space-y-6">
                          {chap.notes.map((n, i) => (
                            <article key={i} className="card-surface p-6" data-testid={`commentary-note-${chap.chapter}-${i}`}>
                              <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                                <div>
                                  <div className="font-serif-display-italic text-lg" style={{ color: "var(--terracotta)" }}>
                                    {n.ref}
                                  </div>
                                  {n.verse_start && (
                                    <div className="text-[0.6rem] uppercase tracking-wider mt-1" style={{ color: "var(--muted)" }}>
                                      {formatVerseRange(n.verse_start, n.verse_end)}
                                    </div>
                                  )}
                                </div>
                                <Link
                                  to={`/read-with-me#day-${n.day_number}`}
                                  className="text-[0.65rem] uppercase tracking-[0.18em] inline-flex items-center gap-1"
                                  style={{ color: "var(--muted)" }}
                                  data-testid={`commentary-source-${n.day_number}`}
                                >
                                  Day {n.day_number} · {n.date} <ArrowRight size={10} />
                                </Link>
                              </div>
                              <p className="text-base leading-[1.75] whitespace-pre-line" style={{ color: "var(--ink-2)" }}>
                                {n.note}
                              </p>
                            </article>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Unparseable references (admins can fix these) */}
            {user.is_admin && data?.unparseable?.length > 0 && (
              <section className="mt-16 pt-8 border-t" style={{ borderColor: "var(--border-subtle)" }}>
                <div className="overline mb-3" style={{ color: "var(--terracotta)" }}>Unparseable refs · admin only</div>
                <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>
                  These verse notes couldn't be sorted into a Bible book — usually because of a typo or unusual format. Fix them in the Read With Me admin and they'll appear in the correct book.
                </p>
                <ul className="space-y-2 text-sm">
                  {data.unparseable.map((n, i) => (
                    <li key={i} className="flex items-baseline gap-3">
                      <code className="px-2 py-0.5 text-xs" style={{ background: "var(--surface-hover)", color: "var(--terracotta)" }}>
                        {n.ref || "(empty)"}
                      </code>
                      <span style={{ color: "var(--ink-2)" }} className="truncate">{n.note.slice(0, 90)}</span>
                      <Link to={`/read-with-me#day-${n.day_number}`} className="text-xs" style={{ color: "var(--muted)" }}>
                        Day {n.day_number}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
